import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import MobileLayout from "@/components/MobileLayout";
import { Flame, Dumbbell, Wind, Heart, ChevronLeft, Check, Calendar, Zap } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { recommendPlan, CuratedPlan, FitnessGoal, FitnessLevel } from "@/lib/fitnessPlans";
import { createMirrorAuthSessionScope } from "@/lib/mirrorAuthSession";
import { fitnessOnboardingPlanId, saveFitnessOnboarding, type FitnessOnboardingGateway } from "@/lib/fitnessOnboardingWriter";
import { notifyFitnessChanged } from "@/lib/fitnessChanges";
import type { Json } from "@/integrations/supabase/types";

// Existing RPC verified against the live schema; the generated client predates it.
type ActivationBuilder = PromiseLike<{ data: unknown; error: unknown }> & {
  abortSignal(signal: AbortSignal): ActivationBuilder;
  setHeader(name: string, value: string): ActivationBuilder;
};
const activationRpc = supabase.rpc.bind(supabase) as unknown as (
  name: "activate_workout_plan", args: { p_plan_id: string; p_new_plan: Json },
) => ActivationBuilder;
const onboardingGateway = (accessToken: string): FitnessOnboardingGateway => ({
  activate: (planId, plan, signal) => activationRpc("activate_workout_plan", { p_plan_id: planId, p_new_plan: plan })
    .setHeader("Authorization", "Bearer " + accessToken).abortSignal(signal),
  saveProfile: (profile, signal) => supabase.from("fitness_profiles").upsert(profile, { onConflict: "user_id" })
    .select("user_id,goal,level,days_per_week,equipment").abortSignal(signal)
    .single().setHeader("Authorization", "Bearer " + accessToken),
});

const GOALS = [
  { key: "lose", icon: Flame, color: "hsl(0 70% 55%)" },
  { key: "muscle", icon: Dumbbell, color: "hsl(187 72% 50%)" },
  { key: "endurance", icon: Wind, color: "hsl(140 60% 50%)" },
  { key: "general", icon: Heart, color: "hsl(265 60% 60%)" },
];

const LEVELS = ["beginner", "intermediate", "advanced"] as const;
const DAYS = [2, 3, 4, 5, 6];
const EQUIPMENT = ["bodyweight", "dumbbells", "barbell", "cables", "full_gym", "home_gym"] as const;

// Map onboarding goal keys to curated plan FitnessGoal type
const GOAL_TO_FITNESS_GOAL: Record<string, FitnessGoal> = {
  lose: "weight_loss",
  muscle: "hypertrophy",
  endurance: "performance",
  general: "strength",
};

const FitnessOnboarding: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [step, setStep] = useState(1);
  const [goal, setGoal] = useState("general");
  const [level, setLevel] = useState("beginner");
  const [days, setDays] = useState(3);
  const [equip, setEquip] = useState<string[]>(["bodyweight"]);
  const [saving, setSaving] = useState(false);
  const saveLock = useRef(false);
  const mounted = useRef(false);
  const authScope = useRef(createMirrorAuthSessionScope());
  useEffect(() => {
    mounted.current = true;
    const scope = authScope.current;
    const observe = (session: unknown) => {
      if (!mounted.current) return;
      if (scope.observeSession(session).changed) { saveLock.current = false; setSaving(false); }
    };
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => observe(session));
    const revision = scope.revision();
    void supabase.auth.getSession().then(result => {
      if (mounted.current && scope.revision() === revision) observe(result.error ? null : result.data.session);
    }).catch(() => { if (mounted.current && scope.revision() === revision) observe(null); });
    return () => { mounted.current = false; scope.invalidate(); subscription.unsubscribe(); };
  }, []);

  const toggleEquip = (e: string) => setEquip(prev => prev.includes(e) ? prev.filter(x => x !== e) : [...prev, e]);

  const recommended = useMemo<CuratedPlan>(() => {
    const fitnessGoal = GOAL_TO_FITNESS_GOAL[goal] || "strength";
    return recommendPlan(fitnessGoal, level as FitnessLevel, days);
  }, [goal, level, days]);

  const handleActivatePlan = async () => {
    if (saveLock.current) return;
    const scope = authScope.current, lease = scope.capture();
    if (!lease) { toast.error(t("fitness.onboarding.saveError")); return; }
    const current = () => mounted.current && scope.isCurrent(lease);
    const assertCurrent = () => { if (!current()) throw new Error("Fitness setup session changed"); };
    saveLock.current = true; setSaving(true);
    const draft = { goal, level, days, equipment: [...equip], plan: recommended,
      planName: t(recommended.i18nKey + ".name"), planDescription: t(recommended.i18nKey + ".desc") };
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      assertCurrent();
      if (error || user?.id !== lease.ownerId) throw new Error("Fitness setup account unconfirmed");
      const session = await supabase.auth.getSession();
      assertCurrent();
      if (session.error || !scope.matchesSession(session.data.session, lease) || !session.data.session) throw new Error("Fitness setup session unconfirmed");
      const gateway = onboardingGateway(session.data.session.access_token);
      const planId = await fitnessOnboardingPlanId(lease.ownerId, lease.authSessionId, draft);
      assertCurrent();
      await saveFitnessOnboarding(lease.ownerId, planId, draft, gateway, assertCurrent);
      assertCurrent(); notifyFitnessChanged(); navigate("/fitness");
    } catch {
      if (current()) toast.error(t("fitness.onboarding.saveError"));
    } finally {
      if (current()) { saveLock.current = false; setSaving(false); }
    }
  };

  return (
    <MobileLayout className="pb-28 pt-8 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[380px] h-[220px] rounded-full opacity-[0.04]" style={{ background: "radial-gradient(ellipse 55% 50% at 50% 50%, hsl(187 72% 50%), transparent)" }} />
      </div>

      <button disabled={saving} onClick={() => step > 1 ? setStep(step - 1) : navigate("/modules")} className="flex items-center gap-1 text-gray-400 mb-4 active:opacity-70">
        <ChevronLeft size={16} /><span className="text-[13px] font-display">{t("common.back")}</span>
      </button>

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <p className="text-[9px] font-display tracking-[0.2em] uppercase mb-0.5" style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("fitness.onboarding.subtitle")}</p>
        <h1 className="font-display text-[26px] font-semibold text-foreground leading-none">{t("fitness.onboarding.title")}</h1>
      </motion.div>

      {/* Progress bar */}
      <div className="flex gap-1.5 mb-6">
        {[1, 2, 3, 4, 5].map(s => (
          <div key={s} className="flex-1 h-1 rounded-full" style={{ background: s <= step ? "hsl(187 72% 50%)" : "hsl(222 18% 18%)" }} />
        ))}
      </div>

      <fieldset disabled={saving} className="contents">
      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-3">
            <p className="font-display text-[16px] font-semibold text-foreground mb-4">{t("fitness.onboarding.goal")}</p>
            {GOALS.map(g => (
              <button key={g.key} onClick={() => { setGoal(g.key); setStep(2); }}
                className="w-full flex items-center gap-4 p-4 rounded-2xl active:scale-[0.98] transition-all"
                style={{ background: goal === g.key ? `${g.color}15` : "hsl(var(--card))", border: `1.5px solid ${goal === g.key ? g.color : "hsl(187 72% 50% / 0.12)"}` }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${g.color}18` }}>
                  <g.icon size={18} style={{ color: g.color }} />
                </div>
                <p className="font-display text-[14px] font-semibold text-foreground">{t(`fitness.onboarding.goal${g.key.charAt(0).toUpperCase() + g.key.slice(1)}`)}</p>
              </button>
            ))}
          </motion.div>
        )}

        {step === 2 && (
          <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-3">
            <p className="font-display text-[16px] font-semibold text-foreground mb-4">{t("fitness.onboarding.level")}</p>
            {LEVELS.map(l => (
              <button key={l} onClick={() => { setLevel(l); setStep(3); }}
                className="w-full p-4 rounded-2xl text-left active:scale-[0.98] transition-all"
                style={{ background: level === l ? "hsl(187 72% 50% / 0.10)" : "hsl(var(--card))", border: `1.5px solid ${level === l ? "hsl(187 72% 50% / 0.50)" : "hsl(187 72% 50% / 0.12)"}` }}>
                <p className="font-display text-[14px] font-semibold text-foreground">{t(`fitness.onboarding.level${l.charAt(0).toUpperCase() + l.slice(1)}`)}</p>
              </button>
            ))}
          </motion.div>
        )}

        {step === 3 && (
          <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <p className="font-display text-[16px] font-semibold text-foreground mb-4">{t("fitness.onboarding.days")}</p>
            <div className="flex gap-2 mb-6">
              {DAYS.map(d => (
                <button key={d} onClick={() => setDays(d)}
                  className="flex-1 py-3 rounded-xl font-display text-[16px] font-bold transition-all"
                  style={{ background: days === d ? "hsl(var(--primary))" : "hsl(var(--card))", color: days === d ? "hsl(var(--background))" : "hsl(218 14% 55%)", border: `1px solid ${days === d ? "hsl(var(--primary))" : "hsl(187 72% 50% / 0.15)"}` }}>
                  {d}
                </button>
              ))}
            </div>
            <button onClick={() => setStep(4)} className="w-full py-3 rounded-xl font-display text-[13px] font-semibold" style={{ background: "hsl(var(--primary))", color: "hsl(var(--background))" }}>
              {t("fitness.onboarding.next")}
            </button>
          </motion.div>
        )}

        {step === 4 && (
          <motion.div key="s4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <p className="font-display text-[16px] font-semibold text-foreground mb-4">{t("fitness.onboarding.equipment")}</p>
            <div className="grid grid-cols-2 gap-2 mb-6">
              {EQUIPMENT.map(e => (
                <button key={e} onClick={() => toggleEquip(e)}
                  className="p-3.5 rounded-xl font-display text-[12px] font-semibold transition-all flex items-center justify-center gap-2"
                  style={{ background: equip.includes(e) ? "hsl(187 72% 50% / 0.12)" : "hsl(var(--card))", border: `1px solid ${equip.includes(e) ? "hsl(187 72% 50% / 0.50)" : "hsl(187 72% 50% / 0.12)"}`, color: equip.includes(e) ? "hsl(187 72% 55%)" : "hsl(218 14% 55%)" }}>
                  {equip.includes(e) && <Check size={12} />}
                  {t(`fitness.onboarding.equip${e.charAt(0).toUpperCase() + e.slice(1).replace("_", "")}`)}
                </button>
              ))}
            </div>
            <button onClick={() => setStep(5)}
              className="w-full py-3.5 rounded-xl font-display text-[14px] font-semibold tracking-wide active:scale-[0.97]"
              style={{ background: "hsl(var(--primary))", color: "hsl(var(--background))" }}>
              {t("fitness.onboarding.next")}
            </button>
          </motion.div>
        )}
        {step === 5 && (
          <motion.div key="s5" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-1" style={{ color: "hsl(187 72% 50% / 0.55)" }}>
              {t("fitness.onboarding.recommendedPlan")}
            </p>
            <p className="font-display text-[16px] font-semibold text-foreground mb-1">{t("fitness.onboarding.planPreview")}</p>

            <div className="rounded-2xl p-5 mb-4 mt-4" style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.22)", boxShadow: "0 0 20px hsl(187 72% 50% / 0.08)" }}>
              <p className="font-display text-[18px] font-bold text-foreground mb-1">{t(recommended.i18nKey + ".name")}</p>
              <p className="text-[12px] font-body text-foreground/50 mb-4">{t(recommended.i18nKey + ".desc")}</p>

              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-display font-semibold" style={{ background: "hsl(187 72% 50% / 0.12)", color: "hsl(187 72% 55%)" }}>
                  <Zap size={10} />
                  {t(`fitness.level.${recommended.level}`)}
                </div>
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-display font-semibold" style={{ background: "hsl(187 72% 50% / 0.12)", color: "hsl(187 72% 55%)" }}>
                  <Calendar size={10} />
                  {recommended.daysPerWeek}x / {t("fitness.onboarding.week")}
                </div>
              </div>

              <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-2" style={{ color: "hsl(187 72% 50% / 0.45)" }}>
                {t("fitness.onboarding.trainingDays")}
              </p>
              {recommended.days.map((day, i) => (
                <div key={i} className="flex items-center gap-2 py-1.5">
                  <Check size={10} style={{ color: "hsl(187 72% 55%)" }} />
                  <span className="text-[12px] font-display text-foreground/70">
                    {day.name} — {day.exercises.length} {t("fitness.plans.exerciseCount")}
                  </span>
                </div>
              ))}
            </div>

            <button onClick={handleActivatePlan} disabled={saving}
              className="w-full py-3.5 rounded-xl font-display text-[14px] font-semibold tracking-wide active:scale-[0.97] disabled:opacity-50"
              style={{ background: "hsl(var(--primary))", color: "hsl(var(--background))" }}>
              {saving ? "..." : t("fitness.onboarding.activatePlan")}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
      </fieldset>
    </MobileLayout>
  );
};

export default FitnessOnboarding;
